import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';
import DateTimePicker from '../DateTimePicker/DateTimePicker';
import IpField from '../IpField/IpField';
import MultiSelect from '../MultiSelect/MultiSelect';
import NumberField from '../NumberField/NumberField';
import Select from '../Select/Select';
import Switch from '../Switch/Switch';
import TextField from '../TextField/TextField';
import TagInputField from '../TagInputField/TagInputField';
import CarNumberField from '../CarNumberField/CarNumberField';

type InputProps =
  | AppTextFieldProps
  | AppNumberFieldProps
  | AppIpFieldProps
  | AppSelectProps
  | AppMultiSelectProps
  | AppSwitchProps
  | AppDateTimePickerProps
  | AppAutoCompleteProps;

export type AppControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = InputProps & {
  variant: ControllerVariant;
  control?: Control<TFieldValues>;
  name: TName;
  rules?: RegisterOptions<TFieldValues>;
  isSms?: boolean;
  onClickSms?: () => void;
};

const AppController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  variant,
  control,
  name,
  rules,
  isSms = false,
  onClickSms,
  ...inputProps
}: AppControllerProps<TFieldValues, TName>) => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    render={({ field, fieldState }) => {
      switch (variant) {
        case 'TextField':
          return (
            <TextField
              id={name}
              {...(inputProps as AppTextFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
              isSms={isSms}
              onClickSms={onClickSms}
            />
          );
        case 'NumberField':
          return (
            <NumberField
              id={name}
              {...(inputProps as AppNumberFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
              name={field.name}
            />
          );
        case 'IpField':
          return (
            <IpField
              id={name}
              {...(inputProps as AppIpFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'Select':
          return (
            <Select
              id={name}
              {...(inputProps as AppSelectProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'MultiSelect':
          return (
            <MultiSelect
              id={name}
              {...(inputProps as AppMultiSelectProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'Switch':
          return (
            <Switch
              id={name}
              {...(inputProps as AppSwitchProps)}
              required={!!rules?.required}
              checked={field.value}
              onChange={field.onChange}
            />
          );
        case 'DateTimePicker':
          return (
            <DateTimePicker
              id={name}
              {...(inputProps as AppDateTimePickerProps)}
              value={field.value}
              required={!!rules?.required}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'TagInputField':
          return (
            <TagInputField
              id={name}
              {...(inputProps as AppAutoCompleteProps)}
              value={field.value}
              required={!!rules?.required}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'CarNumberField':
          return (
            <CarNumberField
              id={name}
              {...(inputProps as CarNumberFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
      }
    }}
  />
);

export default AppController;
